import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Table, Modal, Form, Spinner, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrash, FaBox, FaExclamationTriangle } from 'react-icons/fa';
import { spPackageApi } from '../../api/serviceProviders/spPackageApi';
import { spApi } from '../../api/serviceProviders';

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    id: null,
    package_name: '',
    package_description: '',
    price: '',
    category_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch packages and provider categories in parallel
      const [pkgResponse, catResponse] = await Promise.all([
        spPackageApi.getPackages(),
        spApi.getMyCategories()
      ]);

      // Handle packages
      const pkgData = pkgResponse?.data?.data || pkgResponse?.data || pkgResponse || [];
      setPackages(Array.isArray(pkgData) ? pkgData : []);

      // Handle categories
      const catData = catResponse?.data?.data || catResponse?.data || catResponse || [];
      setCategories(Array.isArray(catData) ? catData : []);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Kuna tatizo kupata taarifa za vifurushi');
      setPackages([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (pkg = null) => {
    if (pkg) {
      setFormData({
        id: pkg.id,
        package_name: pkg.package_name || '',
        package_description: pkg.package_description || '',
        price: pkg.price || '',
        category_id: pkg.category_id || pkg.category?.id || ''
      });
    } else {
      setFormData({
        id: null,
        package_name: '',
        package_description: '',
        price: '',
        category_id: categories.length > 0 ? categories[0].id : ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        package_name: formData.package_name,
        package_description: formData.package_description,
        price: parseFloat(formData.price),
        category_id: parseInt(formData.category_id, 10)
      };

      if (formData.id) {
        await spPackageApi.updatePackage(formData.id, payload);
        toast.success('Kifurushi kimesasishwa kikamilifu!');
      } else {
        await spPackageApi.createPackage(payload);
        toast.success('Kifurushi kipya kimeongezwa!');
      }
      handleCloseModal();
      fetchData();
    } catch (err) {
      console.error('Error saving package:', err);
      toast.error('Kuna tatizo kuhifadhi kifurushi');
    } finally {
      setSubmitting(false);
    }
  };

  // Show confirm delete modal
  const confirmDelete = (pkg) => {
    setItemToDelete(pkg);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await spPackageApi.deletePackage(itemToDelete.id);
      toast.success('Kifurushi kimefutwa kikamilifu!');
      setShowDeleteModal(false);
      setItemToDelete(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting package:', err);
      toast.error('Kuna tatizo kufuta kifurushi');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center mt-5" style={{ minHeight: '300px' }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  return (
    <Container className="mt-4 pb-5">
      <div className="text-center mb-4">
        <h1 className="stylish-heading mb-2" style={{ color: '#4169e1' }}>Vifurushi na Huduma zangu</h1>
        <div className="d-flex justify-content-center mt-3">
          <Button
            variant="primary"
            onClick={() => handleShowModal()}
            style={{ borderRadius: '30px', background: 'linear-gradient(135deg, #4169e1 0%, #3a59c0 100%)', border: 'none', padding: '10px 24px' }}
          >
            <FaPlus className="me-2" /> Ongeza Kifurushi
          </Button>
        </div>
      </div>

      <Card className="shadow-sm border-0">
        <Card.Body>
          {packages.length === 0 ? (
            <div className="text-center py-5">
              <FaBox size={48} className="text-muted opacity-25 mb-3" />
              <h5 className="text-muted">Hakuna vifurushi vyovyote ulivyoweka.</h5>
              <p className="text-muted mb-4">Ongeza vifurushi (Packages) ili wateja waweze kuona huduma zako na gharama zake.</p>
              <Button variant="outline-primary" style={{ borderRadius: '30px' }} onClick={() => handleShowModal()}>
                <FaPlus className="me-2" /> Ongeza Kifurushi Sasa
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Jina la Kifurushi</th>
                    <th>Maelezo</th>
                    <th>Kategoria</th>
                    <th>Bei (TZS)</th>
                    <th className="text-end">Vitendo</th>
                  </tr>
                </thead>
                <tbody>
                  {packages.map((pkg, idx) => {
                    const catName = categories.find(c => c.id === pkg.category_id)?.category
                      || pkg.category?.category
                      || pkg.category?.name
                      || 'N/A';
                    return (
                      <tr key={pkg.id}>
                        <td className="text-muted">{idx + 1}</td>
                        <td className="fw-semibold">{pkg.package_name}</td>
                        <td>
                          <div style={{ maxWidth: '260px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {pkg.package_description}
                          </div>
                        </td>
                        <td>
                          <Badge bg="primary" className="bg-opacity-10 text-primary border border-primary-subtle fw-normal px-2 py-1">
                            {catName}
                          </Badge>
                        </td>
                        <td className="fw-bold text-success">
                          {Number(pkg.price).toLocaleString()}
                        </td>
                        <td className="text-end">
                          <Button
                            variant="light"
                            size="sm"
                            className="me-2 text-primary border"
                            title="Hariri"
                            onClick={() => handleShowModal(pkg)}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="light"
                            size="sm"
                            className="text-danger border"
                            title="Futa"
                            onClick={() => confirmDelete(pkg)}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton style={{ borderBottom: '2px solid #f0f0f0' }}>
            <Modal.Title className="fw-bold" style={{ color: '#4169e1' }}>
              {formData.id ? '✏️ Badili Taarifa za Kifurushi' : '➕ Ongeza Kifurushi Kipya'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Jina la Kifurushi <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="package_name"
                value={formData.package_name}
                onChange={handleChange}
                placeholder="Mfano: Gold Package"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Kategoria <span className="text-danger">*</span></Form.Label>
              <Form.Select
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                required
              >
                <option value="">-- Chagua Kategoria --</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.category || cat.name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Gharama / Bei (TZS) <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder="Mfano: 2500000"
                required
                min="0"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Maelezo (Description) <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="package_description"
                value={formData.package_description}
                onChange={handleChange}
                placeholder="Eleza nini kinapatikana ndani ya hiki kifurushi..."
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer style={{ borderTop: '2px solid #f0f0f0' }}>
            <Button variant="outline-secondary" onClick={handleCloseModal} style={{ borderRadius: '30px', minWidth: '100px' }}>
              Ghairi
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={submitting}
              style={{ borderRadius: '30px', minWidth: '150px', background: 'linear-gradient(135deg, #4169e1, #3a59c0)', border: 'none' }}
            >
              {submitting ? <><Spinner size="sm" animation="border" className="me-2" />Inahifadhi...</> : formData.id ? 'Hifadhi Mabadiliko' : 'Ongeza Kifurushi'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered size="sm">
        <Modal.Body className="text-center p-4">
          <div
            className="mb-3 mx-auto d-flex justify-content-center align-items-center rounded-circle"
            style={{ width: '64px', height: '64px', background: 'rgba(220,53,69,0.1)' }}
          >
            <FaExclamationTriangle size={28} className="text-danger" />
          </div>
          <h5 className="fw-bold mb-1">Futa Kifurushi</h5>
          <p className="text-muted small mb-3">
            Una uhakika unataka kufuta kifurushi cha{' '}
            <strong>"{itemToDelete?.package_name}"</strong>?
            <br />Hatua hii haiwezi kutenduliwa.
          </p>
          <div className="d-flex gap-2 justify-content-center">
            <Button
              variant="outline-secondary"
              onClick={() => setShowDeleteModal(false)}
              style={{ borderRadius: '30px', minWidth: '90px' }}
              disabled={deleting}
            >
              Hapana
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={deleting}
              style={{ borderRadius: '30px', minWidth: '90px' }}
            >
              {deleting ? <><Spinner size="sm" animation="border" className="me-1" />Inafuta...</> : 'Ndiyo, Futa'}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default Packages;
